import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Code, Plus, Save, Table2, Trash2 } from 'lucide-react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import { Input } from 'src/shared/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/shared/components/ui/table'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'src/shared/components/ui/collapsible'
import { Card } from 'src/shared/components/ui/card'
import {
  ToggleGroup,
  ToggleGroupItem,
} from 'src/shared/components/ui/toggle-group'

import { RawTableEditor } from './RawTableEditor'
import { formatDate, parseNum, toStr } from '../utils'

export const DecorationSetupCostTable = ({
  data,
  vendor,
  printCode,
  isLoading,
  onSave,
  onAdd,
  onDelete,
}: {
  data: Array<{
    id: number
    cost?: number | null
    max_colors?: number | null
    created_at: string
  }>
  vendor: string
  printCode: string
  isLoading: boolean
  onSave: (
    updates: Array<{
      id: number
      cost?: number | null
      max_colors?: number | null
    }>
  ) => Promise<void>
  onAdd: (insert: {
    vendor: string
    print_code: string
    cost?: number | null
    max_colors?: number | null
  }) => Promise<void>
  onDelete: (id: number) => Promise<void>
}) => {
  const nextId = useRef(-1)
  const [entryMode, setEntryMode] = useState<'table' | 'raw'>('table')
  const [rows, setRows] = useState<
    Array<{
      id: number
      cost: string
      max_colors: string
      created_at: string
    }>
  >([])
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    setRows(
      data.map((r) => ({
        id: r.id,
        cost: toStr(r.cost),
        max_colors: toStr(r.max_colors),
        created_at: r.created_at,
      }))
    )
  }, [data])

  const update = useCallback(
    (i: number, field: string, value: string) =>
      setRows((prev) =>
        prev.map((r, j) => (j === i ? { ...r, [field]: value } : r))
      ),
    []
  )

  const add = useCallback(
    () =>
      setRows((prev) => [
        ...prev,
        {
          id: nextId.current--,
          cost: '',
          max_colors: '',
          created_at: '',
        },
      ]),
    []
  )

  const remove = useCallback(
    async (i: number) => {
      const r = rows[i]
      if (r?.id > 0 && onDelete) await onDelete(r.id)
      setRows((prev) => prev.filter((_, j) => j !== i))
    },
    [rows, onDelete]
  )

  const save = useCallback(async () => {
    setIsSaving(true)
    try {
      const toUpdate = rows
        .filter((r) => r.id > 0)
        .map((r) => ({
          id: r.id,
          cost: parseNum(r.cost),
          max_colors: parseNum(r.max_colors),
        }))
      if (toUpdate.length) await onSave(toUpdate)
      if (onAdd) {
        for (const r of rows.filter((x) => x.id < 0)) {
          await onAdd({
            vendor,
            print_code: printCode,
            cost: parseNum(r.cost),
            max_colors: parseNum(r.max_colors),
          })
        }
      }
    } finally {
      setIsSaving(false)
    }
  }, [rows, vendor, printCode, onSave, onAdd])

  const inputCls = 'h-9 border-0 bg-transparent focus-visible:ring-1 min-w-0'

  const rawInitialValue = useMemo(
    () =>
      `[\n${data
        .map((r) =>
          JSON.stringify(
            { id: r.id, cost: r.cost, max_colors: r.max_colors },
            null,
            2
          )
        )
        .join(',\n')}\n]`,
    [data]
  )

  const handleRawSave = useCallback(
    async (parsed: Record<string, unknown>[]) => {
      const existingIds = new Set(data.map((r) => r.id))
      const parsedIds = new Set(
        parsed.map((r) => Number(r?.id)).filter((id) => id > 0)
      )
      const toUpdate = parsed
        .filter((r) => Number(r?.id) > 0)
        .map((r) => ({
          id: Number(r.id),
          cost:
            typeof r.cost === 'number'
              ? r.cost
              : r.cost != null
              ? Number(r.cost)
              : null,
          max_colors:
            typeof r.max_colors === 'number'
              ? r.max_colors
              : r.max_colors != null
              ? Number(r.max_colors)
              : null,
        }))
      const toCreate = parsed
        .filter((r) => !r?.id || Number(r.id) <= 0)
        .map((r) => ({
          vendor,
          print_code: printCode,
          cost:
            typeof r.cost === 'number'
              ? r.cost
              : r.cost != null
              ? Number(r.cost)
              : null,
          max_colors:
            typeof r.max_colors === 'number'
              ? r.max_colors
              : r.max_colors != null
              ? Number(r.max_colors)
              : null,
        }))
      const toDelete = [...existingIds].filter((id) => !parsedIds.has(id))
      if (toUpdate.length) await onSave(toUpdate)
      for (const r of toCreate) await onAdd(r)
      for (const id of toDelete) await onDelete(id)
    },
    [data, vendor, printCode, onSave, onAdd, onDelete]
  )

  return (
    <Card>
      <Collapsible defaultOpen>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="w-full justify-between px-4 py-2">
            <span className="font-medium">Decoration setup cost</span>
            <span className="text-muted-foreground">▼</span>
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Column className="gap-4 pt-2">
            <Row className="items-center gap-2">
              <span className="text-sm text-muted-foreground">Edit mode:</span>
              <ToggleGroup
                type="single"
                value={entryMode}
                onValueChange={(v) => v && setEntryMode(v as 'table' | 'raw')}
                variant="outline"
              >
                <ToggleGroupItem value="table">
                  <Table2 className="h-4 w-4 mr-2" />
                  Table
                </ToggleGroupItem>
                <ToggleGroupItem value="raw">
                  <Code className="h-4 w-4 mr-2" />
                  Raw
                </ToggleGroupItem>
              </ToggleGroup>
            </Row>

            {entryMode === 'raw' ? (
              <RawTableEditor
                initialValue={rawInitialValue}
                dataKey={data}
                onSave={handleRawSave}
              />
            ) : (
              <>
                <Column className="border rounded-md overflow-auto max-h-[400px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10" />
                        <TableHead>ID</TableHead>
                        <TableHead>Cost</TableHead>
                        <TableHead>Max colors</TableHead>
                        <TableHead>Created</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.length === 0 && !isLoading ? (
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            className="text-center text-muted-foreground py-6"
                          >
                            No rows
                          </TableCell>
                        </TableRow>
                      ) : (
                        rows.map((row, i) => (
                          <TableRow key={row.id}>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => remove(i)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                            <TableCell className="tabular-nums">
                              {row.id > 0 ? row.id : '–'}
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                value={row.cost}
                                onChange={(e) =>
                                  update(i, 'cost', e.target.value)
                                }
                                className={inputCls}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                value={row.max_colors}
                                onChange={(e) =>
                                  update(i, 'max_colors', e.target.value)
                                }
                                className={inputCls}
                              />
                            </TableCell>
                            <TableCell className="text-muted-foreground tabular-nums">
                              {formatDate(row.created_at)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </Column>
                <Row className="gap-2">
                  <Button variant="secondary" size="sm" onClick={add}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add row
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={save}
                    disabled={isSaving}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </Button>
                </Row>
              </>
            )}
          </Column>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  )
}
